import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { TableVirtuoso } from 'react-virtuoso'
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type SaleContract,
  useGetSaleContractsQuery,
} from '../../store/api/saleContractApi'
import ContractFormDialog from './ContractFormDialog'
import type { SaleContractFormController } from './ContractFormDialog'
import PrintOffDialog from './PrintOffDialog'
import PrintOnDialog from './PrintOnDialog'
import { useAgentBridgeRegistry } from '../../agent/bridgeRegistry'
import BrandState from '../../components/BrandState'
import { usePagePermission } from '../../hooks/usePagePermission'

type FilterKind = 'search' | 'clientCode' | 'contractType' | 'certification' | 'status'
type FinalizedFilter = 'all' | 'yes' | 'no'

/** TablePagination «All» maps to this pageSize (single request; real row count ≤ total). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

/** Compact actions above grid (right-aligned; slightly larger than ultra-tight). */
const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'number' | 'percent'
type ColumnDef = { key: keyof SaleContract; label: string; kind?: ColumnKind; align?: 'right' }

/** Column order + captions match DocConvert §8.5.2 (`frmContractSalesQryLocalS`). */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'contractNo', label: 'Contract Ref' },
  { key: 'buyerRef', label: "Buyer's Ref" },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'clientCode', label: 'Client ID' },
  { key: 'saleOffshoreOnshore', label: 'Client Type' },
  { key: 'clientShort', label: 'Client' },
  { key: 'consigneeName', label: 'Consignee' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'bags', label: 'Bags', kind: 'number', align: 'right' },
  { key: 'packagingCode', label: 'Package Code' },
  { key: 'packaging', label: 'Package VN' },
  { key: 'packagingEng', label: 'Package ENG' },
  { key: 'qualityCode', label: 'Quality' },
  { key: 'certificationCode', label: 'Certification' },
  { key: 'contractType', label: 'Contract Type' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price Unit' },
  { key: 'ptbfDiff', label: 'PTBF diffs', kind: 'number', align: 'right' },
  { key: 'vatTxt', label: 'Applicable VAT' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'fixationCall', label: 'Fixation Call' },
  { key: 'fxRate', label: 'FX', kind: 'number', align: 'right' },
  { key: 'deliveryTerm', label: 'Delivery Terms' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'shipmentDateFrom', label: 'Shipment From', kind: 'date' },
  { key: 'shipmentDateTo', label: 'Shipment To', kind: 'date' },
  { key: 'deliveryDateFrom', label: 'Delivery From', kind: 'date' },
  { key: 'deliveryDateTo', label: 'Delivery To', kind: 'date' },
  { key: 'pss', label: 'PSS Terms' },
  { key: 'paymentWeightTerm', label: 'Payment Weight Terms' },
  { key: 'franchise', label: 'Franchise', kind: 'percent', align: 'right' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'conditionsCode', label: 'Conditions' },
  { key: 'note', label: 'Note' },
  { key: 'sentContractText', label: 'Sent Contract' },
  { key: 'receivedContractText', label: 'Received Contract' },
  { key: 'sentPaperText', label: 'Sent Paper' },
  { key: 'receivedPaperText', label: 'Received Paper' },
  { key: 'futuresMonth2', label: 'Futures Month2' },
  { key: 'fxAuto', label: 'FX Auto', kind: 'number', align: 'right' },
  { key: 'qualityPremium', label: 'Quality Premium', kind: 'number', align: 'right' },
  { key: 'certPremium', label: 'Cert Premium', kind: 'number', align: 'right' },
  { key: 'packagePremium', label: 'Package Premium', kind: 'number', align: 'right' },
  { key: 'freight', label: 'Freight', kind: 'number', align: 'right' },
  { key: 'logisticsCost', label: 'Logistics Cost', kind: 'number', align: 'right' },
  { key: 'insuranceCost', label: 'Insurance Cost', kind: 'number', align: 'right' },
  { key: 'financeCost', label: 'Finance Cost', kind: 'number', align: 'right' },
  { key: 'bankCost', label: 'Bank Charge', kind: 'number', align: 'right' },
  { key: 'weightingCost', label: 'Weighing Cost', kind: 'number', align: 'right' },
  { key: 'g2FobDiffBulk', label: 'G2 FOB Fiff Bulk', kind: 'number', align: 'right' },
  { key: 'totalLot', label: 'Total Lot', kind: 'number', align: 'right' },
  { key: 'lotFixed', label: 'Lot Fixed', kind: 'number', align: 'right' },
  { key: 'lotToFix', label: 'Lot To Fix', kind: 'number', align: 'right' },
  { key: 'avgFuturesPrice', label: 'Avg Futures Price', kind: 'number', align: 'right' },
  { key: 'impliedDiffs', label: 'Implied Diffs', kind: 'number', align: 'right' },
  { key: 'kgFixed', label: 'Kg Fixed', kind: 'number', align: 'right' },
  { key: 'kgToFix', label: 'Kg To Fix', kind: 'number', align: 'right' },
  { key: 'avgPrice', label: 'Avg Price', kind: 'number', align: 'right' },
  { key: 'description', label: 'Description' },
  { key: 'finalizedText', label: 'Finalized' },
  { key: 'finalizedDate', label: 'Finalized Date', kind: 'date' },
  { key: 'includedRoyal', label: 'Included Royal Fee ?' },
  { key: 'royalFee', label: 'Royal Fee', kind: 'number', align: 'right' },
  { key: 'matchingKg', label: 'Matched Quantity', kind: 'number', align: 'right' },
  { key: 'unmatchedKg', label: 'Unmatched Quantity', kind: 'number', align: 'right' },
  { key: 'brokerCode', label: 'Broker Code' },
  { key: 'brokerFirm', label: 'Broker Name' },
  { key: 'brokerRef', label: "Broker's Ref" },
  { key: 'commission', label: 'Commission', kind: 'number', align: 'right' },
  { key: 'traderName', label: 'Trader In Charge' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'date' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<keyof SaleContract>([
  'price',
  'ptbfDiff',
  'fxRate',
  'fxAuto',
  'avgFuturesPrice',
  'impliedDiffs',
  'avgPrice',
])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`.slice(-2)
  return `${day}-${month}-${year}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const distinctValues = (rows: SaleContract[], key: keyof SaleContract): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const sumNumericColumn = (rows: SaleContract[], key: keyof SaleContract): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const rowMatchesSearch = (row: SaleContract, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    const value = row[col.key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

interface ContractListViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function ContractListView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: ContractListViewProps) {
  const { canEdit } = usePagePermission()
  const { registerBridge, unregisterBridge, setActiveModule } = useAgentBridgeRegistry()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  /** Default «All»: request large pageSize (backend no longer caps list pageSize). */
  const [rowsPerPage, setRowsPerPage] = useState(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [clientCodeFilter, setClientCodeFilter] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')
  const [certificationFilter, setCertificationFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) =>
    setFilterPopover({ kind, anchor: e.currentTarget })
  const hasActiveFilters = Boolean(
    searchTerm.trim() || clientCodeFilter.trim() || contractTypeFilter || certificationFilter || statusFilter,
  )
  const clearAllFilters = () => {
    setSearchTerm('')
    setClientCodeFilter('')
    setContractTypeFilter('')
    setCertificationFilter('')
    setStatusFilter('')
    setPage(0)
    closeFilterPopover()
  }

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'new' | 'copy' | 'edit'>('new')
  const formControllerRef = useRef<SaleContractFormController | null>(null)

  const [printOffOpen, setPrintOffOpen] = useState(false)
  const [printOnOpen, setPrintOnOpen] = useState(false)

  const {
    data: contractList,
    isLoading,
    isFetching,
    error,
  } = useGetSaleContractsQuery({
    page: 1,
    pageSize: ALL_ROWS_PAGE_SIZE,
  })

  const baseRows = useMemo(() => contractList?.items ?? [], [contractList])
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        if (clientCodeFilter.trim() && row.clientCode !== clientCodeFilter.trim()) return false
        if (contractTypeFilter && row.contractType !== contractTypeFilter) return false
        if (certificationFilter && row.certificationCode !== certificationFilter) return false
        if (statusFilter && row.finalizedText !== statusFilter) return false
        return true
      }),
    [
      baseRows,
      certificationFilter,
      clientCodeFilter,
      contractTypeFilter,
      searchTerm,
      statusFilter,
    ],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key)).map((col) => [
          col.key,
          sumNumericColumn(filteredRows, col.key),
        ]),
      ),
    [filteredRows],
  )
  const clientCodeOptions = useMemo(() => distinctValues(baseRows, 'clientCode'), [baseRows])
  const contractTypeOptions = useMemo(() => distinctValues(baseRows, 'contractType'), [baseRows])
  const certificationOptions = useMemo(() => distinctValues(baseRows, 'certificationCode'), [baseRows])
  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const selectedRow = useMemo<SaleContract | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  const openDialog = (mode: 'new' | 'copy' | 'edit') => {
    if ((mode === 'copy' || mode === 'edit') && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    if (mode === 'edit' && selectedRow && (selectedRow.finalizedText ?? '').trim().toUpperCase() !== 'N') {
      window.alert('This contract is finalized! No edit')
      return
    }
    setDialogMode(mode)
    setDialogOpen(true)
  }

  const cellValue = (row: SaleContract, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'percent' && typeof val === 'number') return `${(val * 100).toFixed(2)}%`
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'boolean') return val ? 'Yes' : 'No'
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  // Ref pattern: bridge methods đọc state mới nhất qua ref, useEffect chỉ đăng ký
  // 1 lần với deps stable. Tránh deps đổi mỗi render → infinite register loop.
  const bridgeStateRef = useRef({ rows, selectedRow, dialogOpen, openDialog })
  bridgeStateRef.current = { rows, selectedRow, dialogOpen, openDialog }

  useEffect(() => {
    setActiveModule('sale-contract')
    registerBridge('sale-contract', {
      openForm: async (mode) => {
        bridgeStateRef.current.openDialog(mode)
        return `Đã mở Sale Contract form (${mode}).`
      },
      selectRow: async (selector) => {
        const { rows } = bridgeStateRef.current
        const normalized = String(selector).trim().toLowerCase()
        const found = rows.find(
          (row) =>
            row.id === Number(selector) ||
            row.contractNo.toLowerCase() === normalized ||
            String(row.id) === normalized,
        )
        if (!found) throw new Error(`Không tìm thấy Sale Contract với selector: ${selector}`)
        setSelectedId(found.id)
        return `Đã chọn Sale Contract #${found.id} (${found.contractNo}).`
      },
      setField: async (field, value) => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form chưa mở.')
        return formControllerRef.current.setField(field, value)
      },
      saveForm: async () => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form chưa mở.')
        return formControllerRef.current.save()
      },
      cancelForm: async () => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form chưa mở.')
        return formControllerRef.current.cancel()
      },
      getState: async () => {
        const { dialogOpen, selectedRow } = bridgeStateRef.current
        if (!dialogOpen || !formControllerRef.current) {
          return selectedRow
            ? `Sale list ready. selectedId=${selectedRow.id}, contractNo=${selectedRow.contractNo}.`
            : 'Sale list ready. Chưa chọn dòng.'
        }
        return formControllerRef.current.getState()
      },
    })

    return () => {
      unregisterBridge('sale-contract')
      setActiveModule(null)
    }
  }, [registerBridge, unregisterBridge, setActiveModule])

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('search', e)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          <Button
            size="small"
            variant={clientCodeFilter.trim() ? 'contained' : 'outlined'}
            color={clientCodeFilter.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('clientCode', e)}
            sx={filterBarBtnSx}
          >
            Client
          </Button>
          <Button
            size="small"
            variant={contractTypeFilter ? 'contained' : 'outlined'}
            color={contractTypeFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('contractType', e)}
            sx={filterBarBtnSx}
          >
            Contract Type
          </Button>
          <Button
            size="small"
            variant={certificationFilter ? 'contained' : 'outlined'}
            color={certificationFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('certification', e)}
            sx={filterBarBtnSx}
          >
            Certification
          </Button>
          <Button
            size="small"
            variant={statusFilter ? 'contained' : 'outlined'}
            color={statusFilter ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openFilterPopover('status', e)}
            sx={filterBarBtnSx}
          >
            Status
          </Button>
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => openDialog('new')}>
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={() => openDialog('copy')}
          >
            Copy
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!selectedRow || !canEdit}
            onClick={() => openDialog('edit')}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
            disabled={!selectedRow}
            onClick={() => setPrintOffOpen(true)}
          >
            Print-Off
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
            disabled={!selectedRow}
            onClick={() => setPrintOnOpen(true)}
          >
            Print-On
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load sale contracts.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        {/* Filter bar + pagination */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

      {/* Filter popovers */}
      <Popover
        open={filterPopover !== null}
        anchorEl={filterPopover?.anchor ?? null}
        onClose={closeFilterPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
      >
        {filterPopover?.kind === 'search' && (
          <Stack spacing={1}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              label="Search contracts"
              placeholder="Contract no, client..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
            />
            <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>
              Clear Search
            </Button>
          </Stack>
        )}
        {filterPopover?.kind === 'clientCode' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={clientCodeFilter}
              onChange={(e) => { setClientCodeFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
            >
              <MenuItem value="">All / Clear</MenuItem>
              {clientCodeOptions.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {filterPopover?.kind === 'contractType' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Contract Type</InputLabel>
            <Select
              label="Contract Type"
              value={contractTypeFilter}
              onChange={(e) => { setContractTypeFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
            >
              <MenuItem value="">All / Clear</MenuItem>
              {contractTypeOptions.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {filterPopover?.kind === 'certification' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Certification</InputLabel>
            <Select
              label="Certification"
              value={certificationFilter}
              onChange={(e) => { setCertificationFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
            >
              <MenuItem value="">All / Clear</MenuItem>
              {certificationOptions.map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {filterPopover?.kind === 'status' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}
            >
              <MenuItem value="">All / Clear</MenuItem>
              {distinctValues(baseRows, 'finalizedText').map((value) => (
                <MenuItem key={value} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Popover>

      {/* Virtualized table (react-virtuoso) — no 100-row cap */}
      <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
        {error ? (
          <BrandState
            tone="error"
            title="Could not load Sale Contracts"
            description="Please refresh the page or contact the system administrator if the issue continues."
          />
        ) : isLoading && baseRows.length === 0 ? (
          <BrandState tone="loading" title="Loading Sale Contracts" description="Preparing contract data from the ERP workspace." />
        ) : rows.length === 0 ? (
          <BrandState
            title="No Sale Contracts found"
            description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
          />
        ) : (
          <TableVirtuoso
          key={gridKey}
          style={{ height: '100%' }}
          data={rows}
          fixedHeaderContent={() => (
            <TableRow>
              {TABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          )}
          fixedFooterContent={() => (
            <TableRow>
              {TABLE_COLUMNS.map((col, index) => {
                const total = totalRowValues.get(col.key)
                const val = total == null ? '' : formatAccessNumber(total)
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      py: 0.5,
                      bgcolor: 'background.paper',
                      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {index === 0 ? 'Total' : val}
                  </TableCell>
                )
              })}
            </TableRow>
          )}
          itemContent={(_: number, row: SaleContract) =>
            TABLE_COLUMNS.map((col) => {
              const val = cellValue(row, col)
              return (
                <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                  <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                    {val || '-'}
                  </Box>
                </TableCell>
              )
            })
          }
          components={{
            Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 6800, width: '100%' }} />,
            // react-virtuoso expects plain HTML section components; MUI types are stricter, so we pass `component`
            // and loosen typing to avoid React type duplication issues in this repo.
            TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
            TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
            TableRow: (props: any) => {
              const idx = Number((props as any)['data-item-index'])
              const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
              return (
                <TableRow
                  {...props}
                  hover
                  selected={rowId != null && rowId === selectedId}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (rowId != null) setSelectedId(rowId)
                  }}
                />
              )
            },
          }}
          />
        )}
      </Box>
      </Box>

      {/* Dialogs */}
      <ContractFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode={dialogMode}
        contractId={selectedRow?.id}
        contractNo={selectedRow?.contractNo}
        controllerRef={formControllerRef}
      />
      <PrintOffDialog
        open={printOffOpen}
        onClose={() => setPrintOffOpen(false)}
        contractId={selectedRow?.id ?? null}
        contractNo={selectedRow?.contractNo ?? ''}
        clientShort={selectedRow?.clientShort ?? ''}
        canEdit={canEdit}
      />
      <PrintOnDialog
        open={printOnOpen}
        onClose={() => setPrintOnOpen(false)}
        contractId={selectedRow?.id ?? null}
        contractNo={selectedRow?.contractNo ?? ''}
        clientShort={selectedRow?.clientShort ?? ''}
        canEdit={canEdit}
      />
    </Box>
  )
}
